/**
 * Microsoft Clarity nav heatmap tracking.
 *
 * Menus open as overlays on the same URL, so heatmap tools don't see them as
 * distinct pages. We instead set a Clarity custom tag when a nav item is
 * engaged, capturing top-level AND nested items as a full breadcrumb path so
 * sub-links keep their parent context and don't collide across menus.
 *
 * Tag: key `menu`, value `<surface>_<breadcrumb>`
 *   e.g. mobile_open, desktop_shop, desktop_shop_womens_boots, mobile_women_gumboots
 *
 * Fire-and-forget: every handler is wrapped so it can never break the menus.
 */
(() => {
  "use strict";

  // "About Us" -> "about_us"; "" for anything that isn't a non-empty string.
  const slug = (title) =>
    typeof title === "string"
      ? title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
      : "";

  // An element's own label (icons are <svg>, so textContent stays clean).
  const labelOf = (el) => (el && el.textContent ? el.textContent : "").trim();

  // Set the `menu` tag from a breadcrumb (outermost menu -> engaged item).
  // window.clarity is injected async and may be absent (consent / not loaded);
  // an empty path means our selectors stopped matching the markup.
  const fireMenu = (surface, segments) => {
    const path = segments.map(slug).filter(Boolean).join("_");
    if (!path) {
      console.warn(`could not derive a ${surface} menu name from`, JSON.stringify(segments), "— menu markup may have changed.");
      return;
    }
    if (typeof window.clarity !== "function") {
      console.warn("window.clarity unavailable; menu tags not sent (Clarity blocked by consent or not yet loaded).");
      return;
    }
    try {
      window.clarity("set", "menu", `${surface}_${path}`);
    } catch (error) {
      console.error("window.clarity threw while setting the menu tag.", error);
    }
  };

  // Desktop dropdown + mega menu: the DOM is nested, so build the breadcrumb by
  // walking ancestors up to the header link list — each <details> adds its own
  // <summary>, and a mega category adds its <a> heading (a direct child of an
  // <li.v-stack> in .mega-menu__nav, with items in a sibling <ul>).
  // `node` is an <a> leaf (click) or a <summary> (details open).
  const desktopPath = (node) => {
    const root = node.closest(".header__link-list");
    if (!root) return [];
    const segments = [];
    let cur = node.parentElement;
    while (cur && cur !== root && root.contains(cur)) {
      if (cur.tagName === "DETAILS") {
        const summary = cur.querySelector(":scope > summary");
        if (summary && summary !== node) segments.unshift(labelOf(summary));
      } else if (cur.matches("li.v-stack") && cur.closest(".mega-menu__nav")) {
        const heading = cur.querySelector(":scope > a");
        if (heading && heading !== node && !heading.contains(node)) {
          segments.unshift(labelOf(heading));
        }
      }
      cur = cur.parentElement;
    }
    segments.push(labelOf(node));
    return segments;
  };

  // Desktop menu opens (top-level + nested sub-sections). <details> fires the
  // native `toggle` event, which doesn't bubble — hence capture phase. Gate on
  // `details.open` (not `event.newState`, which isn't set in every browser).
  document.addEventListener(
    "toggle",
    (event) => {
      try {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open) return;
        if (!details.closest(".header__link-list")) return;
        const summary = details.querySelector(":scope > summary");
        if (summary) fireMenu("desktop", desktopPath(summary));
      } catch (error) {
        console.error("desktop menu toggle handler failed.", error);
      }
    },
    true,
  );

  // Mobile drawer: sub-panels render as flat siblings (not nested), so we track
  // drill-down via the data-panel depth. Expand buttons are "<depth>-<n>"
  // (truncate to parent levels, then set this depth); back buttons are a bare
  // "<targetDepth>" (truncate to it). Leaf links read the stack for their path.
  const drawerStack = [];
  const drawerButtonLabel = (btn) => {
    const span = btn.querySelector("span:not(.circle-chevron)");
    return labelOf(span || btn);
  };

  // Expanded mobile nav (accordion): one level — children live in
  // .accordion-content inside the .accordion-parent that holds the toggle.
  const accordionTitle = (el) => {
    const label = el.querySelector(".accordion-link-title, .accordion-promo-title, span:not(.icon)");
    return labelOf(label || el);
  };
  const accordionPath = (link) => {
    const segments = [];
    const parent = link.closest(".accordion-parent");
    if (parent) {
      const toggle = parent.querySelector(":scope > button.accordion-toggle, :scope > a.accordion-link");
      if (toggle && toggle !== link) segments.push(accordionTitle(toggle));
    }
    segments.push(accordionTitle(link));
    return segments;
  };

  // Single delegated click handler, ordered most-specific first.
  document.addEventListener("click", (event) => {
    try {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Hamburger — opens the mobile slideout menu.
      if (target.closest('button[aria-controls="header-sidebar-menu"]')) {
        fireMenu("mobile", ["open"]);
        return;
      }

      // Drawer expand / back buttons — maintain the navigation stack.
      const panelButton = target.closest("button[data-panel]");
      if (panelButton) {
        const dataPanel = panelButton.getAttribute("data-panel") || "";
        const drill = dataPanel.match(/^(\d+)-\d+$/);
        if (drill) {
          const depth = parseInt(drill[1], 10);
          drawerStack.length = depth - 1;
          drawerStack[depth - 1] = drawerButtonLabel(panelButton);
          fireMenu("mobile", drawerStack);
        } else if (/^\d+$/.test(dataPanel)) {
          drawerStack.length = parseInt(dataPanel, 10);
        }
        return;
      }

      // Expanded mobile nav top-level expandable items.
      const accordionToggle = target.closest(".accordion-toggle");
      if (accordionToggle) {
        fireMenu("mobile", [accordionTitle(accordionToggle)]);
        return;
      }

      // Links — top-level and nested — across every nav variant.
      const link = target.closest("a");
      if (!link) return;

      // Desktop: top-level links, dropdown items, mega categories/items.
      if (link.closest(".header__link-list")) {
        fireMenu("desktop", desktopPath(link));
        return;
      }
      // Expanded mobile accordion: top-level links and nested children/promos.
      if (link.matches("a.accordion-link") || link.closest(".accordion-child")) {
        fireMenu("mobile", accordionPath(link));
        return;
      }
      // Drawer top-level links (li.first-child) have no parent path.
      if (link.closest(".panel-list__wrapper li.first-child")) {
        fireMenu("mobile", [labelOf(link)]);
        return;
      }
      // Drawer nested links sit inside a sub-panel — prepend the tracked path.
      if (link.closest(".panel__wrapper")) {
        fireMenu("mobile", [...drawerStack, labelOf(link)]);
      }
    } catch (error) {
      console.error("nav click handler failed.", error);
    }
  });
})();
